'use strict'

import { ref, computed, watch } from 'vue'
import { useHead } from '@unhead/vue'

import { LinkRelation, LinkTarget } from '@xcel-next/core/enums'

import { useSourcepointStore } from '../stores/sourcepoint'

type PagestripWindow = Window & {
  pagestrip?: {
    render: (el: HTMLElement | null, options: {
      preset: string,
      publisher: { embedId: string },
      router: { history: string },
      content: { lookupId: string }
    }) => void
  }
}

// Shared singleton state — multiple components (AdaNativeHeader, AdaPagestripMain) call this
// composable independently. Keeping state at module level ensures they share the same scriptLoaded
// flag and rendered-container tracking instead of racing with isolated instances.
const pagestripQuery = ref<string | null>(null)
const pagestripHeaderId = ref<string | null>(null)
const pagestripMainId = ref<string | null>(null)
const embedId = ref('adalliance-produkt-modern')
const scriptLoaded = ref(false)
const renderedContainers = new Set<string>()
const timeoutCounter = ref(0)

export function useAdaPagestrip () {
  const sourcepointStore = useSourcepointStore()

  const target = ref<LinkTarget.blank>(LinkTarget.blank)
  const rel = ref<LinkRelation.nofollow>(LinkRelation.nofollow)

  const consentsExist = computed(() => sourcepointStore.consentsExist)

  const loadPagestrip = computed(() =>
    consentsExist.value && (pagestripQuery.value || pagestripHeaderId.value || pagestripMainId.value)
  )

  useHead(computed(() => {
    if (!loadPagestrip.value) return {}
    return {
      script: [{
        'data-vmid': 'ada-pagestrip-loader',
        key: 'ada-pagestrip-loader',
        src: 'https://j2.pagestrip.com/public/dist/player/loader.min.js',
        async: true,
        onload: () => { scriptLoaded.value = true }
      }]
    }
  }))

  watch(consentsExist, (newValue) => {
    if (loadPagestrip.value && newValue) {
      initPagestrip()
    }
  })

  watch(scriptLoaded, (newValue) => {
    if (loadPagestrip.value && newValue) {
      initPagestrip()
    }
  })

  function initPagestrip () {
    if (!consentsExist.value || !scriptLoaded.value) return

    document.body.dataset.adaPagestrip = 'true' // prevent min-height on above-the-fold ad slots

    // working example queryString: ?ps=eosZ6x2u:JJUtv68Y:adalliance-produkt-modern
    if (pagestripQuery.value) {
      if (renderedContainers.has('pagestrip-header') && renderedContainers.has('pagestrip-main')) return
      const [lookupIdHeader, lookupIdMain, forceEmbedId] = pagestripQuery.value.split(':')
      if (forceEmbedId) embedId.value = forceEmbedId
      if (!isPagestripAvailable()) return
      renderContainer('pagestrip-header', embedId.value, lookupIdHeader)
      renderContainer('pagestrip-main', embedId.value, lookupIdMain)
    } else {
      if (pagestripHeaderId.value && !renderedContainers.has('pagestrip-header')) {
        if (!isPagestripAvailable()) return
        renderContainer('pagestrip-header', embedId.value, pagestripHeaderId.value)
      }
      if (pagestripMainId.value && !renderedContainers.has('pagestrip-main')) {
        if (!isPagestripAvailable()) return
        renderContainer('pagestrip-main', embedId.value, pagestripMainId.value)
      }
    }
  }

  function renderContainer (id: string, embedId: string, lookupId: string | undefined) {
    if (!lookupId) return
    ;(window as PagestripWindow).pagestrip?.render(document.getElementById(id), {
      preset: 'widget',
      publisher: { embedId },
      router: { history: 'memory' },
      content: { lookupId }
    })
    renderedContainers.add(id)
  }

  function isPagestripAvailable (): boolean {
    if (!(window as PagestripWindow).pagestrip) {
      setTimeout(() => {
        timeoutCounter.value++
        if (timeoutCounter.value < 5) initPagestrip()
      }, 500)
      return false
    }
    return true
  }

  return {
    pagestripQuery,
    pagestripHeaderId,
    pagestripMainId,
    target,
    rel,
    initPagestrip
  }
}
